import { PERIOD_OPTIONS, DEFAULT_PERIOD_DAYS } from "./analytics.js";

// URL shapes:
//   /                                search screen
//   /u/<name>                        dashboard
//   /u/<a>/vs/<b>[/vs/<c>[/vs/<d>]]  comparison, up to MAX_COMPARE_PROFILES others
//   ?p=<days>                        period filter, omitted when it is the default
//
// The app has two screens and no nested routes, so this is history.pushState
// plus popstate rather than a router dependency.

const PROFILE_PREFIX = "u";
const COMPARE_SEGMENT = "vs";

// Four profiles in total. Every extra profile is one more queued history import
// on the server, and four columns is where the comparison screen stops being
// readable — server.js repeats the cap in its route pattern.
export const MAX_COMPARE_PROFILES = 3;

// Hevy usernames are a single path segment. Anything with a slash, a dot-segment
// or wrapping whitespace came from a hand-edited URL, not from us.
function cleanSegment(raw) {
  let decoded;
  try {
    decoded = decodeURIComponent(String(raw || ""));
  } catch {
    return ""; // a hand-mangled escape like /u/%zz
  }
  const value = decoded.trim().replace(/^@+/, "");
  if (!value || value === "." || value === ".." || /[/\\]/.test(value)) return "";
  return value;
}

function readPeriod(search) {
  const raw = new URLSearchParams(search || "").get("p");
  const days = Number(raw);
  return PERIOD_OPTIONS.includes(days) ? days : DEFAULT_PERIOD_DAYS;
}

// Compared profiles are a list. A bare string is still accepted so a caller
// comparing a single profile does not have to wrap it.
function compareList(compareWith) {
  return [].concat(compareWith || []).filter(Boolean).map(String);
}

export function parseLocation(location = window.location) {
  const periodDays = readPeriod(location.search);
  const parts = String(location.pathname || "/").split("/").filter(Boolean);
  const searchScreen = { screen: "search", username: "", compareWith: [], periodDays };

  if (parts[0] !== PROFILE_PREFIX) return searchScreen;

  const username = cleanSegment(parts[1]);
  if (!username) return searchScreen;

  const compareWith = [];
  const seen = new Set([username.toLowerCase()]);
  for (let i = 2; i + 1 < parts.length; i += 2) {
    if (parts[i] !== COMPARE_SEGMENT) break;
    if (compareWith.length >= MAX_COMPARE_PROFILES) break;
    // An unusable or repeated segment is skipped rather than ending the list:
    // the profiles named after it were still asked for.
    const name = cleanSegment(parts[i + 1]);
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    compareWith.push(name);
  }

  if (compareWith.length) return { screen: "comparison", username, compareWith, periodDays };
  return { screen: "dashboard", username, compareWith: [], periodDays };
}

export function buildPath({ screen, username, compareWith, periodDays } = {}) {
  const query = PERIOD_OPTIONS.includes(Number(periodDays)) && Number(periodDays) !== DEFAULT_PERIOD_DAYS
    ? `?p=${Number(periodDays)}`
    : "";

  if (screen === "search" || !username) return `/${query}`;

  const base = `/${PROFILE_PREFIX}/${encodeURIComponent(username)}`;
  const others = compareList(compareWith).slice(0, MAX_COMPARE_PROFILES);
  if (screen === "comparison" && others.length) {
    const tail = others.map((name) => `/${COMPARE_SEGMENT}/${encodeURIComponent(name)}`).join("");
    return `${base}${tail}${query}`;
  }
  return `${base}${query}`;
}

export function currentPath(location = window.location) {
  return `${location.pathname}${location.search}`;
}

// Page title and description. The backend builds the same strings for the HTML
// it serves (getShellMeta in server.js) — it cannot import this module, since
// the Docker image ships server.js without the frontend sources. A test pins
// the two together so they cannot drift apart unnoticed.
export const APP_NAME = "Hevy Analyzer";
// The landing title carries the name and what the product does. The name alone
// spent the strongest ranking field on two words, and "analyze <...> Hevy
// profile" is the phrase people actually type. Profile routes keep the short
// form: there the username is the part worth reading in an unfurl card.
export const APP_TITLE = "Hevy Analyzer — analyze any public Hevy profile";
export const APP_TAGLINE = "Muscle balance, volume trends, PRs and equipment for any public Hevy profile. No account, no password — just a username.";

// "@a and @b", "@a, @b and @c" — the same list the server builds.
function joinHandles(names) {
  const handles = names.map((name) => `@${name}`);
  if (handles.length < 2) return handles.join("");
  return `${handles.slice(0, -1).join(", ")} and ${handles[handles.length - 1]}`;
}

export function buildTitle({ username, compareWith } = {}) {
  if (!username) return APP_TITLE;
  const others = compareList(compareWith);
  if (others.length) return `@${[username, ...others].join(" vs @")} · ${APP_NAME}`;
  return `@${username} · ${APP_NAME}`;
}

export function buildDescription({ username, compareWith } = {}) {
  if (!username) return APP_TAGLINE;
  const others = compareList(compareWith);
  if (others.length) {
    return `${joinHandles([username, ...others])} side by side — muscle split, training volume and personal records.`;
  }
  return `Training breakdown for @${username} — muscle balance, volume trends, PRs and equipment.`;
}
